import React, { useEffect, useState } from 'react'
import _ from 'lodash';

import useGetLocations from '../../../../hooks/locations/useGetLocations';
import useGetEmploymentIndustries from '../../../../hooks/misc/useGetEmploymentIndustries';
import useGetSchools from '../../../../hooks/misc/useGetSchools';
import useGetAIRecommendations_JobTitles from '../../../../hooks/misc/useGetAIRecommendations_JobTitles';

import MainLayout from '../../../layouts/Main';

import JobDetailsForm from '../../../sub_components/job_portals/JobDetails';
import JobApplicationDetailsForm from '../../../sub_components/job_portals/JobApplicationDetails';
import JobAudienceForm from '../../../sub_components/job_portals/JobAudience';

import { IJobPostFormData, JobPostDetailsTab, JobPostStatus, TRequiredJobPostFormField } from '../../../../interfaces/job_posts';
import { ISortQuery, IAttachedFileWithUrl, IJobTitle } from '../../../../interfaces/services';
import useSaveJobPost from '../../../../hooks/job_posts/useSaveJobPost';
import { getDateString } from '../../../../helpers/utility';

const NewJobPage = () => {
  const tabsList: JobPostDetailsTab[] = Object.values(JobPostDetailsTab).slice(0, -1);

  const requiredFields: TRequiredJobPostFormField[] = ['title', 'description', 'status'];

  const [currentTab, setCurrentTab] = useState<JobPostDetailsTab>(tabsList[0]);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [attachedFiles, setAttachedFiles] = useState<IAttachedFileWithUrl[]>([]);
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const [occupationRecommendations, setOccupationRecommendations] = useState<IJobTitle[]>([]);
  const [isOccupationRecommendationsLoading, setIsOccupationRecommendationsLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState<IJobPostFormData>({
    id: null,
    location_id: { value: { value: '', label: '' }, error: '' },
    title: { value: '', error: '' },
    occupation: { value: { value: '', label: '' }, error: '' },
    description: { value: '', error: '' },
    open_positions: { value: '', error: '' },
    industry_id: { value: { value: '', label: '' }, error: '' },
    onsite_remote: { value: { value: '', label: '' }, error: '' },
    employment_type: { value: { value: '', label: '' }, error: '' },
    responsibilities: { value: '', error: '' },
    qualifications: { value: '', error: '' },
    job_level: { value: { value: '', label: '' }, error: '' },
    salary_min: { value: '', error: '' },
    salary_max: { value: '', error: '' },
    salary_currency: { value: { value: '', label: '' }, error: '' },
    salary_period: { value: { value: '', label: '' }, error: '' },
    experience_level: { value: '', error: '' },
    keywords: { value: '', error: '' },
    advert_start: { value: '', error: '' },
    advert_end: { value: '', error: '' },
    job_start: { value: '', error: '' },
    require_cover_letter: { value: { value: '', label: '' }, error: '' },
    contact_title: { value: '', error: '' },
    contact_email: { value: '', error: '' },
    web_link: { value: '', error: '' },
    associated_schools: { value: [], error: '' },
  });

  const sortQuery: ISortQuery = { sort_key: 'identifier', sort_order: 'ASC' };

  const { data: locations, isLoading: isLocationsLoading, error: locationsError } = useGetLocations('organization', undefined, sortQuery);
  const { data: employmentIndustries, isLoading: isEmploymentIndustriesLoading, error: employmentIndustriesError } = useGetEmploymentIndustries();
  const { data: schools, isLoading: isSchoolsLoading, error: schoolsError } = useGetSchools();

  const { mutate: saveJobPost } = useSaveJobPost();
  const { mutate: getAIRecommendationsJobTitles } = useGetAIRecommendations_JobTitles();

  const error = locationsError || employmentIndustriesError || schoolsError;

  const getOccupationRecommendations = (query: string, limit: number, delay: number, callback: (data: IJobTitle[]) => void) => {
    if (!query.trim().length) {
      callback([]);
      return;
    }

    const debouncedGetOccupationRecommendations = _.debounce(() => {
      getAIRecommendationsJobTitles({
        setIsProcessing: setIsOccupationRecommendationsLoading,
        payload: { query, limit },
        successCallback: callback,
      });
    }, delay);

    debouncedGetOccupationRecommendations();
  }

  useEffect(() => {
    getOccupationRecommendations(
      formData.title.value,
      10,
      2000,
      (data: IJobTitle[]) => {
        setOccupationRecommendations(data);
      },
    );
  }, [formData.title.value]); // eslint-disable-line

  const renderTabContent = () => {
    switch (currentTab) {
      case JobPostDetailsTab.JOB_DETAILS:
        return <JobDetailsForm
          formData={formData}
          isFormDisabled={false}
          occupationRecommendations={occupationRecommendations}
          isOccupationRecommendationsLoading={isOccupationRecommendationsLoading}
          attachedFiles={attachedFiles}
          filesToUpload={filesToUpload}
          setAttachedFiles={setAttachedFiles}
          setFilesToUpload={setFilesToUpload}
          setFormData={setFormData}
          employmentIndustries={employmentIndustries || []}
          locations={locations || []}
          isLoading={isLocationsLoading || isEmploymentIndustriesLoading}
        />;
      case JobPostDetailsTab.APPLICATION_DETAILS:
        return <JobApplicationDetailsForm
          formData={formData}
          isFormDisabled={false}
          setFormData={setFormData}
        />;
      case JobPostDetailsTab.AUDIENCE:
        return <JobAudienceForm
          formData={formData}
          isFormDisabled={false}
          setFormData={setFormData}
          schools={schools || []}
          isSchoolsLoading={isSchoolsLoading}
        />;
    }
  }

  const checkRequiredFields = () => {
    type TFormDataKeys = keyof IJobPostFormData;
    let isValid = true;
    let newFormData = { ...formData };

    requiredFields.forEach(field => {
      const formField = formData[field as TFormDataKeys];
      if (formField && typeof formField === 'object' && 'value' in formField && 'error' in formField) {
        const isEmpty = typeof formField.value === 'string'
          ? formField.value.trim() === ''
          : typeof formField.value === 'object' && !Array.isArray(formField.value) && (!formField.value || !('value' in formField.value));

        if (isEmpty) {
          isValid = false;
        }
        (newFormData[field as TFormDataKeys] as { error: string }).error = isEmpty ? 'This field is required' : '';
      }
    });

    setFormData(newFormData);
    return isValid;
  }

  const isOkToSave = () => {
    return checkRequiredFields() && !isProcessing && !isOccupationRecommendationsLoading;
  }

  const submitJobPost = (type: JobPostStatus) => {
    const multiPartFormData = new FormData();

    const jobStartDate = formData.job_start.value ? getDateString(formData.job_start.value, 'yyyy-mm-dd') : null;
    const advertStartDate = formData.advert_start.value ? getDateString(formData.advert_start.value, 'yyyy-mm-dd') : null;
    const advertEndDate = formData.advert_end.value ? getDateString(formData.advert_end.value, 'yyyy-mm-dd') : null;

    multiPartFormData.append('status', type);
    multiPartFormData.append('title', formData.title.value || '');
    multiPartFormData.append('occupation', formData.occupation.value.value || '');
    multiPartFormData.append('description', formData.description.value || '');
    multiPartFormData.append('responsibilities', formData.responsibilities.value || '');
    multiPartFormData.append('qualifications', formData.qualifications.value || '');
    multiPartFormData.append('location_id', formData.location_id.value.value || '');
    multiPartFormData.append('industry_id', formData.industry_id.value.value || '');
    multiPartFormData.append('onsite_remote', formData.onsite_remote.value.value || '');
    multiPartFormData.append('employment_type', formData.employment_type.value.value || '');
    multiPartFormData.append('job_level', formData.job_level.value.value || '');
    multiPartFormData.append('open_positions', formData.open_positions.value || '');
    multiPartFormData.append('salary_min', formData.salary_min.value || '');
    multiPartFormData.append('salary_max', formData.salary_max.value || '');
    multiPartFormData.append('salary_currency', formData.salary_currency.value.value || '');
    multiPartFormData.append('salary_period', formData.salary_period.value.value || '');
    multiPartFormData.append('experience_level', formData.experience_level.value || '');
    multiPartFormData.append('keywords', formData.keywords.value || '');
    multiPartFormData.append('advert_start', advertStartDate || '');
    multiPartFormData.append('advert_end', advertEndDate || '');
    multiPartFormData.append('job_start', jobStartDate || '');
    multiPartFormData.append('require_cover_letter', formData.require_cover_letter.value.value || '');
    multiPartFormData.append('contact_title', formData.contact_title.value || '');
    multiPartFormData.append('contact_email', formData.contact_email.value || '');
    multiPartFormData.append('web_link', formData.web_link.value || '');
    formData.associated_schools.value.forEach(school => {
      multiPartFormData.append('associated_school_ids', school.value);
    });

    filesToUpload.forEach(file => {
      multiPartFormData.append('files', file);
    });

    attachedFiles.forEach(file => {
      multiPartFormData.append('attached_file_ids', file.id.toString());
    });

    saveJobPost({
      setIsProcessing,
      setErrorMessage,
      payload: multiPartFormData,
    });
  }

  const handleSaveDraft = () => {
    if (isOkToSave()) {
      submitJobPost(JobPostStatus.DRAFT);
    }
  }

  const handlePost = () => {
    if (isOkToSave()) {
      submitJobPost(JobPostStatus.ACTIVE);
    }
  }

  return (
    <MainLayout>
      <div className="immployer__common_details__layout">
        <div className="immployer__form_details__head">
          <h2 className="color_dark">Add a Job</h2>

          <div className="immployer__form_details__head__actions">
            <button
              className="immployer__btn immployer__btn__secondary"
              onClick={handleSaveDraft}
            >
              Save Draft
            </button>
            <button
              className="immployer__btn immployer__btn__primary"
              onClick={handlePost}
            >
              Post
            </button>
          </div>
        </div>
        <div className="immployer__common_details__tabs__container">
          <div className="immployer__tabs">
            {tabsList.map((tab, index) =>
              <button
                key={index}
                className={`immployer__tab ${currentTab === tab ? "immployer__tab__active" : ""}`}
                onClick={() => setCurrentTab(tab)}
              >
                {tab}
              </button>
            )}
          </div>
        </div>
        <div className="immployer__common_details__content">
          {
            error
              ? <div className="immployer__common_details__error_view">
                <br /><br />
                <h4 className="color_dark text_center">Error occured while fetching the data.</h4>
                <h4 className="color_dark text_center">Please try again later.</h4>
              </div>
              : renderTabContent()
          }
        </div>
      </div>
    </MainLayout>
  )
}

export default NewJobPage;